import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { downloadExcel } from "react-export-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { BiDownload, BiSearch } from "react-icons/bi";
import moment from "moment";

import { Colors } from "../../../assets/Colors";
import { getMinKlien, klienSelector } from "../../master/klien/klienSlice";
import {
  getSummaryV2,
  operationalSummarySelector,
} from "./OperationalSummarySlice";

// Sumber data: JR V2 (JrHeader/JrLine, status >= 100) lewat getOperationalSummaryV2.
// Endpoint lama getSummaryJR/getDetailJR membaca TJobRequest (sistem JR lama) yang tak lagi diisi.

const nf = (n) =>
  new Intl.NumberFormat("id-ID", { maximumFractionDigits: 4 }).format(Number(n) || 0);
const rp = (n) => "Rp " + nf(n);
// Quantity/usage selalu 4 angka di belakang koma.
const nq = (n) =>
  new Intl.NumberFormat("id-ID", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(Number(n) || 0);

const activityLabel = (name) => (name || "").replace(/_/g, " ");

const EMPTY = [];

const JENIS = [
  { value: "Summary", label: "Summary" },
  { value: "Detail", label: "Detail" },
];

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38, borderRadius: 8, fontSize: 14 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Summary: per No. WBS, item yang sama (Activity + Item + Uom + Rate) dijumlahkan.
function buildSummary(data) {
  const byWbs = new Map();
  data.forEach((jr) => {
    const wbs = jr.WSBNo || "-";
    if (!byWbs.has(wbs)) byWbs.set(wbs, { items: new Map(), total: 0 });
    const group = byWbs.get(wbs);
    (jr.Lines || []).forEach((l) => {
      const key = [l.ActivityName, l.ItemName, l.Uom, l.Rate].join("|");
      const g = group.items.get(key) || {
        activity: activityLabel(l.ActivityName),
        item: l.ItemName,
        uom: l.Uom,
        rate: l.Rate,
        qty: 0,
        subTotal: 0,
      };
      g.qty += Number(l.Qty) || 0;
      g.subTotal += Number(l.SubTotal) || 0;
      group.items.set(key, g);
      group.total += Number(l.SubTotal) || 0;
    });
  });
  return [...byWbs.entries()].map(([wbs, g]) => ({ wbs, total: g.total, items: [...g.items.values()] }));
}

// Detail: satu baris per item JR.
function buildDetail(data) {
  const rows = [];
  data.forEach((jr) => {
    (jr.Lines || []).forEach((l) => {
      rows.push({
        date: jr.WorkDate,
        jrId: jr.JrID,
        activity: activityLabel(l.ActivityName),
        item: l.ItemName,
        wbs: jr.WSBNo || "-",
        qty: l.Qty,
        uom: l.Uom,
        rate: l.Rate,
        subTotal: l.SubTotal,
      });
    });
  });
  return rows;
}

const OperationalSummaryPage = () => {
  const dispatch = useDispatch();
  const summary = useSelector(operationalSummarySelector);
  const klien = useSelector(klienSelector);

  const [month, setMonth] = useState(new Date());
  const [jenis, setJenis] = useState("Summary");
  const [klienID, setKlienID] = useState("");
  // Filter yang dipakai saat data terakhir dimuat (judul tabel & nama file Excel).
  const [loaded, setLoaded] = useState({ month: new Date(), klienID: "" });

  const klienOptions = [{ value: "", label: "Semua Client" }, ...(klien.minData || [])];
  const klienLabel = (id) => klienOptions.find((o) => o.value === id)?.label || id;

  useEffect(() => {
    dispatch(getMinKlien({ search: "" }));
    openData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openData() {
    setLoaded({ month, klienID });
    dispatch(
      getSummaryV2({
        Year: month.getFullYear(),
        Month: month.getMonth() + 1,
        KlienID: klienID,
      })
    );
  }

  const data = (summary.v2 && summary.v2.Data) || EMPTY;
  const grandTotal = (summary.v2 && summary.v2.GrandTotal) || 0;
  const summaryGroups = useMemo(() => buildSummary(data), [data]);
  const detailRows = useMemo(() => buildDetail(data), [data]);
  const periode = moment(loaded.month).format("MMMM YYYY");
  const loading = summary.isV2Loading;

  function handleDownloadExcel() {
    let header;
    const body = [];
    if (jenis === "Summary") {
      header = ["No", "Activity", "Item", "Quantity", "Uom", "Rate", "Sub Total"];
      summaryGroups.forEach((g) => {
        body.push(["No WBS: " + g.wbs, "", "", "", "", "", ""]);
        g.items.forEach((it, idx) => {
          body.push([idx + 1, it.activity, it.item, it.qty, it.uom, it.rate, it.subTotal]);
        });
        body.push(["", "", "", "", "", "Sub Total", g.total]);
      });
      body.push(["", "", "", "", "", "Grand Total", grandTotal]);
    } else {
      header = ["No", "Work Date", "Job Request No.", "Activity", "Item", "No WBS", "Quantity", "Uom", "Rate", "Sub Total"];
      detailRows.forEach((r, idx) => {
        body.push([idx + 1, r.date, r.jrId, r.activity, r.item, r.wbs, r.qty, r.uom, r.rate, r.subTotal]);
      });
      body.push(["", "", "", "", "", "", "", "", "Grand Total", grandTotal]);
    }
    downloadExcel({
      fileName: `Summary_JR_${jenis}_${moment(loaded.month).format("YYYY-MM")}`,
      sheet: "data",
      tablePayload: { header, body },
    });
  }

  const colCount = jenis === "Summary" ? 7 : 10;

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      {/* Judul */}
      <div className="py-4">
        <h1 className="text-lg font-semibold" style={{ color: Colors.primary }}>
          Summary Job Request
        </h1>
        <p className="text-xs text-gray-500">
          Rekap Job Request yang sudah disetujui per bulan kerja.
        </p>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl border p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-72">
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Client</label>
            <Select
              value={klienOptions.filter(({ value }) => value === klienID)}
              onChange={(e) => setKlienID(e.value)}
              options={klienOptions}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Bulan</label>
            <DatePicker
              selected={month}
              onChange={(d) => setMonth(new Date(d))}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              className="w-44 h-[38px] border border-gray-300 rounded-lg px-3 text-sm outline-none focus:border-orange-400"
            />
          </div>
          <button
            type="button"
            onClick={openData}
            disabled={loading}
            className="flex items-center gap-1.5 h-[38px] px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
            style={{ background: Colors.primary }}
          >
            <BiSearch /> {loading ? "Memuat…" : "Buka Data"}
          </button>

          <div className="flex items-end gap-3 ml-auto">
            <div>
              <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Tampilan</label>
              <div className="inline-flex h-[38px] rounded-lg border border-gray-300 overflow-hidden">
                {JENIS.map((j) => (
                  <button
                    key={j.value}
                    type="button"
                    onClick={() => setJenis(j.value)}
                    className={
                      "px-4 text-sm font-medium transition-colors " +
                      (jenis === j.value ? "text-white" : "text-gray-600 bg-white hover:bg-gray-50")
                    }
                    style={jenis === j.value ? { background: Colors.primary } : undefined}
                  >
                    {j.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={handleDownloadExcel}
              disabled={!data.length || loading}
              className="flex items-center gap-1.5 h-[38px] px-4 rounded-lg border text-sm font-medium bg-white disabled:opacity-40"
              style={{ borderColor: Colors.primary, color: Colors.primary }}
            >
              <BiDownload /> Download Excel
            </button>
          </div>
        </div>
      </div>

      {/* Ringkasan */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <Stat label="Jumlah Job Request" value={loading ? "…" : nf(data.length)} />
        <Stat label="Jumlah Item" value={loading ? "…" : nf(detailRows.length)} />
        <Stat label="Grand Total" value={loading ? "…" : rp(grandTotal)} strong />
      </div>

      {/* Tabel */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div
          className="px-5 py-3 border-b text-sm flex flex-wrap items-center justify-between gap-2"
          style={{ background: Colors.primary + "15", color: Colors.primary }}
        >
          <span className="font-semibold">
            {jenis} — {periode}
          </span>
          <span className="text-xs text-gray-600">{klienLabel(loaded.klienID)}</span>
        </div>

        <div className="overflow-auto max-h-[65vh]">
          <table className="text-sm border-collapse w-full">
            <thead className="sticky top-0 z-10">
              {jenis === "Summary" ? (
                <tr className="bg-gray-100 text-gray-600 text-xs uppercase">
                  <Th className="text-center w-12">No</Th>
                  <Th>Activity</Th>
                  <Th>Item</Th>
                  <Th className="text-right">Quantity</Th>
                  <Th className="text-center">Uom</Th>
                  <Th className="text-right">Rate</Th>
                  <Th className="text-right">Sub Total</Th>
                </tr>
              ) : (
                <tr className="bg-gray-100 text-gray-600 text-xs uppercase">
                  <Th className="text-center w-12">No</Th>
                  <Th>Work Date</Th>
                  <Th>Job Request No.</Th>
                  <Th>Activity</Th>
                  <Th>Item</Th>
                  <Th>No WBS</Th>
                  <Th className="text-right">Quantity</Th>
                  <Th className="text-center">Uom</Th>
                  <Th className="text-right">Rate</Th>
                  <Th className="text-right">Sub Total</Th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading || !data.length ? (
                <tr>
                  <td colSpan={colCount} className="py-14 text-center text-gray-400 text-sm">
                    {loading
                      ? "Memuat data…"
                      : summary.errorMessage || `Tidak ada Job Request yang disetujui untuk ${periode}.`}
                  </td>
                </tr>
              ) : jenis === "Summary" ? (
                summaryGroups.map((g) => (
                  <React.Fragment key={g.wbs}>
                    <tr className="bg-gray-50">
                      <td colSpan={colCount} className="px-3 py-2 border-b border-gray-200 text-xs font-semibold text-gray-700">
                        No WBS: <span className="font-mono">{g.wbs}</span>
                      </td>
                    </tr>
                    {g.items.map((it, idx) => (
                      <tr key={g.wbs + idx} className="hover:bg-orange-50/40">
                        <Td className="text-center text-gray-500">{idx + 1}</Td>
                        <Td>{it.activity}</Td>
                        <Td>{it.item}</Td>
                        <Td className="text-right tabular-nums">{nq(it.qty)}</Td>
                        <Td className="text-center">{it.uom}</Td>
                        <Td className="text-right tabular-nums">{nf(it.rate)}</Td>
                        <Td className="text-right tabular-nums">{nf(it.subTotal)}</Td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50 text-blue-800 font-semibold">
                      <td colSpan={colCount - 1} className="px-3 py-1.5 text-right border-b border-gray-200">
                        Sub Total {g.wbs !== "-" ? g.wbs : ""}
                      </td>
                      <td className="px-3 py-1.5 text-right border-b border-gray-200 tabular-nums whitespace-nowrap">
                        {nf(g.total)}
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                detailRows.map((r, idx) => (
                  <tr key={r.jrId + idx} className="odd:bg-white even:bg-gray-50 hover:bg-orange-50/40">
                    <Td className="text-center text-gray-500">{idx + 1}</Td>
                    <Td className="whitespace-nowrap">{moment(r.date).format("DD-MM-YYYY")}</Td>
                    <Td className="font-mono text-xs whitespace-nowrap">{r.jrId}</Td>
                    <Td>{r.activity}</Td>
                    <Td>{r.item}</Td>
                    <Td className="font-mono text-xs">{r.wbs}</Td>
                    <Td className="text-right tabular-nums">{nq(r.qty)}</Td>
                    <Td className="text-center">{r.uom}</Td>
                    <Td className="text-right tabular-nums">{nf(r.rate)}</Td>
                    <Td className="text-right tabular-nums">{nf(r.subTotal)}</Td>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && data.length > 0 && (
              <tfoot className="sticky bottom-0">
                <tr className="text-white font-semibold" style={{ background: Colors.primary }}>
                  <td colSpan={colCount - 1} className="px-3 py-2.5 text-right">
                    Grand Total
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{rp(grandTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

function Th({ children, className = "" }) {
  return (
    <th className={`px-3 py-2.5 font-semibold border-b border-gray-200 whitespace-nowrap text-left ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 border-b border-gray-100 text-gray-800 ${className}`}>{children}</td>;
}

function Stat({ label, value, strong }) {
  return (
    <div className="bg-white rounded-xl border px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{label}</p>
      <p
        className={`tabular-nums ${strong ? "text-xl font-bold" : "text-xl font-semibold text-gray-800"}`}
        style={strong ? { color: Colors.primary } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

export default OperationalSummaryPage;
